/**
 * Access to the window.sessionStorage object in components: Makes it
 * easy to get/set session-only values (like settings in a query field, sort directions, ....)
 *
 * To use the functions, you need to provide a prefix to prevent overlaps with other session
 * access functions.
 */

import { ref, watch, type Ref } from 'vue';

/**
 * This method creates a single dynamic reference variable to a browser-session-backed
 * value.
 *
 * Usage:
 *
 * ```
 * const myRefVal = useBrowserSessionValue('my-value', 42); // Ref<number>
 * console.log(myRefVal.value);
 * myRefVal.value = 45;
 * ```
 *
 * All changes are stored in the browser's session storage, and restored when
 * the app is starting up again.
 * The value is reactive and can also be used in templates and v-model values.
 *
 * @param key
 * @param defaultValue
 * @returns
 */
export function useBrowserSessionValue<T>(key: string, defaultValue: T) {
    const { getSessionValue, setSessionValue } = useBrowserSession('');
    const val: Ref<T> = ref(getSessionValue(key, defaultValue));

    const update = (newVal: T) => {
        setSessionValue(key, newVal);
    };

    watch(
        val,
        (newVal) => {
            update(newVal);
        },
        { deep: true },
    );
    return val;
}

export function useBrowserSession(keyPrefix: string) {
    return {
        getSessionValue: (key: string, defaultValue: any = null) =>
            getSessionValue(sessionValueKey(keyPrefix, key), defaultValue),
        setSessionValue: (key: string, value: any) => setSessionValue(sessionValueKey(keyPrefix, key), value),
        clearSessionValue: (key: string) => clearSessionValue(sessionValueKey(keyPrefix, key)),
        clearAllSessionStorage,
    };
}

function sessionValueKey(prefix: string, key: string): string {
    if (prefix) {
        return `${prefix}-${key}`;
    } else {
        return key;
    }
}

/**
 * Returns a value from the session by key, or a default value.
 *
 * @param {String} key
 * @param {any} defaultValue
 * @returns {any} The stored value, if any
 */
function getSessionValue(key: string, defaultValue: any = null) {
    const rawVal = window.sessionStorage?.getItem(key) ?? null;
    return rawVal ? JSON.parse(rawVal) : defaultValue;
}

/**
 * Sets a value in the session. Note that all values are serialzed (json.stringify), as the
 * session storage only allows string values.
 * @param {String} key
 * @param {any} value
 */
function setSessionValue(key: string, value: any) {
    if (value !== undefined) {
        window.sessionStorage?.setItem(key, JSON.stringify(value));
    } else {
        clearSessionValue(key);
    }
}

/**
 * Removes an entry in the session by key
 * @param {String} key
 */
function clearSessionValue(key: string) {
    window.sessionStorage?.removeItem(key);
}

function clearAllSessionStorage() {
    window.sessionStorage?.clear();
}
